import type { Opportunity } from "@/model/DataModel"
import { formatQuantity, roundTo } from "@/utils/format"
import { AreaUnit, convertArea, convertPricePerArea } from "@/utils/unitSystem"

export enum OpportunityUnitField {
	LandArea = "superficy",
	BuildableArea = "constructible",
	PricePerLandArea = "pricePerSqft",
	PricePerBuildableArea = "pricePerConstructibleSqft",
}

type TranslateFn = (key: string) => string

const opportunityUnitFields = new Set(Object.values(OpportunityUnitField))

export function isOpportunityUnitField(field: string): field is OpportunityUnitField {
	return opportunityUnitFields.has(field as OpportunityUnitField)
}

export function formatOpportunityArea(value: number | null | undefined, areaUnit: AreaUnit): string {
	if (!value) {
		return ""
	}

	const convertedValue = convertArea(value, AreaUnit.SquareFeet, areaUnit)

	return formatQuantity(Math.round(convertedValue))
}

export function formatOpportunityPricePerArea(value: number | null | undefined, areaUnit: AreaUnit): string {
	if (value == null || !Number.isFinite(value)) {
		return ""
	}

	const convertedValue = convertPricePerArea(value, AreaUnit.SquareFeet, areaUnit)
	const roundedValue = roundTo(convertedValue, 1)

	return roundedValue != null ? `${formatQuantity(roundedValue, { maximumFractionDigits: 1 })} $` : ""
}

export function formatOpportunityUnitField(
	field: OpportunityUnitField,
	opportunity: Opportunity,
	areaUnit: AreaUnit
): string {
	switch (field) {
		case OpportunityUnitField.LandArea:
			return formatOpportunityArea(opportunity.superficy, areaUnit)
		case OpportunityUnitField.BuildableArea:
			return formatOpportunityArea(opportunity.constructible, areaUnit)
		case OpportunityUnitField.PricePerLandArea:
			return formatOpportunityPricePerArea(opportunity.pricePerSqft, areaUnit)
		case OpportunityUnitField.PricePerBuildableArea:
			return formatOpportunityPricePerArea(opportunity.pricePerConstructibleSqft, areaUnit)
	}
}

export function opportunityUnitFieldLabel(
	field: OpportunityUnitField,
	t: TranslateFn,
	areaUnitSymbol: string,
	pricePerAreaUnitSymbol: string
): string {
	const label = t(`opportunities.unitFields.${field}`)

	switch (field) {
		case OpportunityUnitField.LandArea:
		case OpportunityUnitField.BuildableArea:
			return `${label} (${areaUnitSymbol})`
		case OpportunityUnitField.PricePerLandArea:
		case OpportunityUnitField.PricePerBuildableArea:
			return `${pricePerAreaUnitSymbol} ${label}`
	}
}
